/**
 * @name handleVote
 * @description Odešle hlas na server a podle odpovědi zobrazí výsledky, pokud je uživatel přihlášen.
 * Pokud ne, zobrazí výzvu k přihlášení. Ukládá cookie s informací, že uživatel již hlasoval.
 *
 * @param event
 * @returns {Promise<void>}
 */

import * as Sentry from '@sentry/browser';
import {
  CLASS_ANSWER_STATEMENT,
  CLASS_FOOTER_TOTAL_VOTES,
  CLASS_SINGLE_ANSWER,
  meiroEvents,
} from '@box/article/EnquiryBars/enquiryCommons';
import { EnquiryQuestion, VoteJson, voteUrl } from '@box/article/EnquiryBars/enquiryApi';
import {
  checkDataError,
  checkResponseError,
  handleError,
} from '@box/article/EnquiryBars/errorHandling';
import Cookies from 'js-cookie';
import showResult from '@box/article/EnquiryBars/showResult';
import showLoginLink from '@box/article/EnquiryBars/showLoginLink';
import voteWord from '@box/article/EnquiryBars/voteWord';

export const voteHandler = (
  event: MouseEvent,
  enquiry: HTMLElement,
  answersWrapper: HTMLDivElement,
  voteNotice: HTMLDivElement,
  questionId: string,
  questionText: string,
  isLogged: boolean,
  enquiryEnabled: boolean,
  answers: NodeListOf<HTMLDivElement>,
  voteHandlerListener: (this: HTMLDivElement, event: MouseEvent) => void
) => {
  event.preventDefault();
  const targetElement = event.target;

  if (
    targetElement instanceof Element &&
    (targetElement.classList.contains(CLASS_ANSWER_STATEMENT) ||
      (targetElement.parentElement !== null &&
        targetElement.parentElement.classList.contains(CLASS_ANSWER_STATEMENT)))
  ) {
    // Po kliknutí na vybranou odpověď se odstraní event listener pro všechny odpovědi, nastaví se hláška pro uživatele a uloží data o vybrané odpovědi ze šablony.
    answersWrapper.removeEventListener('click', voteHandlerListener);
    voteNotice.innerText = 'Odesílání hlasu...';
    let answerId: string = '';
    let answerText: string = '';
    const singleAnswerEl = targetElement.closest(`.${CLASS_SINGLE_ANSWER}`);
    if (singleAnswerEl) {
      answerId = (singleAnswerEl as HTMLElement).dataset.answerId as string;
      answerText = (singleAnswerEl as HTMLElement).dataset.answerText as string;
    }

    // Odeslání hlasu a zobrazení výsledků nebo výzvy k přihlášení v závislosti na tom, zda je uživatel přihlášen.
    fetch(voteUrl(answerId, questionId))
      .then((response) => {
        checkResponseError(response.status);
        response
          .json()
          .then((replyData) => {
            const jsonData = replyData as VoteJson;
            checkDataError(jsonData.ok);

            // V případě úspěšného odeslání hlasu se informace o anketě a vybrané odpovědi uloží do cookie.
            // Cookie má platnost 7 dní, po tu dobu uživatel nemůže hlasovat znovu.
            Cookies.set(`enquiry-${questionId}`, answerId, { expires: 7 });
            const selectedAnswerServer = jsonData.id;
            const selectedAnswerData = jsonData.data[selectedAnswerServer];
            const questionData = selectedAnswerData[jsonData.eid];

            // Pokud je uživatel přihlášen, rovnou se zobrazí výsledky - použijí se data, která přijdou jako odpověď po odeslání hlasu.
            // Pokud ne, zobrazí se výzva k přihlášení.
            if (isLogged) {
              answers.forEach((answer) => {
                const answerLoopId = answer.dataset.answerId as string;
                const percent = `${Math.round(
                  parseInt((questionData as EnquiryQuestion)[answerLoopId].qperc_val, 10)
                )}%`;
                voteNotice.innerText = '';
                showResult(answer, percent, answerId);
              });
            } else {
              showLoginLink(
                enquiry,
                answersWrapper,
                voteNotice,
                questionId,
                questionText,
                enquiryEnabled
              );
            }

            const votesTotalServer = parseInt(jsonData.data[selectedAnswerServer].total as string);
            const footerWithVotes = enquiry.querySelector(`.${CLASS_FOOTER_TOTAL_VOTES}`);

            if (footerWithVotes) {
              (footerWithVotes as HTMLElement).innerText = `Celkově ${votesTotalServer} ${voteWord(
                votesTotalServer
              )}`;
            }

            if (window.dataLayer && window.didomiOnReady !== undefined) {
              void window.dataLayer.push({
                event: 'form_input_success',
                form: {
                  id: answerId,
                  name: questionText,
                  result: answerText,
                },
              });
            }

            if (meiroEvents) {
              meiroEvents.track('customEvent', {
                action: `poll voted: ${questionText}`,
                answer: answerText,
              });
            }
          })
          .catch((err) => {
            Sentry.captureException(err);
          });
      })
      .catch((err) => {
        Sentry.captureException(err);
        if (err instanceof Error) {
          handleError(err.message, voteNotice);
        }
      });
  }
};
